import { useEffect, useState } from 'react';

import type { OpsManager } from '@/services/opsRota/types';
import { trackOpsRosterChanged } from '@/util/vexo';

import { useAllManagers, useManagerMutations } from '../hooks/useOpsRota';
import {
  DELETE_MANAGER_TITLE,
  deleteManagerText,
  ROSTER_EMPTY,
  ROSTER_LOAD_ERROR,
  ROSTER_TITLE,
} from '../messages';
import {
  Badge,
  DangerButton,
  DangerLink,
  DialogContent,
  DialogFooter,
  DialogMessage,
  ErrorBox,
  LinkButton,
  Muted,
  RosterDetail,
  RosterList,
  RosterName,
  RosterRow,
  RosterRowMain,
  RowActions,
  SecondaryButton,
  TextButton,
  Toolbar,
} from '../Operations.styles';

import ErrorList from './ErrorList';
import OpsDialog from './OpsDialog';
import RosterManagerForm, { messagesOf } from './RosterManagerForm';

// The prototype has no roster screen. This is a minimal one in the
// Operations tokens, flagged as not designed: list everyone (active and
// inactive), add, edit name / phone / email, deactivate or reactivate, and
// delete (graphy refuses while the manager has schedules).

type Editing = { kind: 'add' } | { kind: 'edit'; id: number } | null;

type Props = {
  /** Focus goes back here on close (the "Manage managers" link). */
  returnFocus?: HTMLElement | null;
  onClose: () => void;
};

/** "Manage managers", opened from the Operations card header. */
const RosterModal = ({ returnFocus, onClose }: Props) => {
  const query = useAllManagers();
  const { update, remove } = useManagerMutations();
  const [editing, setEditing] = useState<Editing>(null);
  const [rowError, setRowError] = useState<{
    id: number;
    messages: string[];
  } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<OpsManager | null>(null);
  // The control focus returns to once a form or the delete confirm closes
  // (its test id: the button may have been unmounted meanwhile).
  const [refocus, setRefocus] = useState<string | null>(null);

  useEffect(() => {
    if (!refocus) return;
    document.querySelector<HTMLElement>(`[data-testid="${refocus}"]`)?.focus();
    setRefocus(null);
  }, [refocus]);

  const closeForm = () => {
    setRefocus(
      editing?.kind === 'edit' ? `opsRosterEdit-${editing.id}` : 'opsRosterAdd',
    );
    setEditing(null);
  };

  const managers = query.data ?? [];
  const formOpen = editing !== null;

  const toggle = async (m: OpsManager) => {
    if (busyId !== null) return;
    setBusyId(m.id);
    setRowError(null);
    try {
      await update.mutateAsync({ id: m.id, patch: { active: !m.active } });
      trackOpsRosterChanged({ action: m.active ? 'deactivate' : 'reactivate' });
    } catch (err) {
      setRowError({ id: m.id, messages: messagesOf(err) });
    } finally {
      setBusyId(null);
    }
  };

  // The dialog stays open (Delete disabled) until graphy answers. A 422
  // ("<Name> has schedules. Deactivate instead.") shows under the row.
  const confirmDelete = async () => {
    if (!deleting || remove.isPending) return;
    const target = deleting;
    setRowError(null);
    try {
      await remove.mutateAsync(target.id);
      trackOpsRosterChanged({ action: 'delete' });
      // The row is gone: land on Add.
      setRefocus('opsRosterAdd');
    } catch (err) {
      setRowError({ id: target.id, messages: messagesOf(err) });
      setRefocus(`opsRosterDelete-${target.id}`);
    } finally {
      setDeleting(null);
    }
  };

  let list;
  if (query.isPending) {
    list = <Muted data-testid="opsRosterLoading">Loading…</Muted>;
  } else if (query.isError && !query.data) {
    list = (
      <ErrorBox role="alert" data-testid="opsRosterLoadError">
        {ROSTER_LOAD_ERROR}
        <LinkButton
          type="button"
          data-testid="opsRosterRetry"
          onClick={() => query.refetch()}
        >
          Retry
        </LinkButton>
      </ErrorBox>
    );
  } else if (managers.length === 0 && editing?.kind !== 'add') {
    list = <Muted data-testid="opsRosterEmpty">{ROSTER_EMPTY}</Muted>;
  } else {
    list = (
      <RosterList aria-label="Operations managers" data-testid="opsRosterList">
        {managers.map((m) =>
          editing?.kind === 'edit' && editing.id === m.id ? (
            <RosterRow key={m.id} data-testid={`opsRosterRow-${m.id}`}>
              <RosterManagerForm
                manager={m}
                onCancel={closeForm}
                onSaved={closeForm}
              />
            </RosterRow>
          ) : (
            <RosterRow
              key={m.id}
              data-testid={`opsRosterRow-${m.id}`}
              data-active={m.active}
              aria-busy={busyId === m.id || undefined}
            >
              <RosterRowMain>
                <div>
                  <RosterName>
                    {m.full_name}
                    <Badge
                      tone={m.active ? 'active' : 'inactive'}
                      data-testid={`opsRosterBadge-${m.id}`}
                    >
                      {m.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </RosterName>
                  <RosterDetail>
                    {m.phone ?? 'No number on file'} ·{' '}
                    {m.email ?? 'No email on file'}
                  </RosterDetail>
                </div>
                <RowActions>
                  <LinkButton
                    type="button"
                    aria-label={`Edit ${m.full_name}`}
                    disabled={formOpen || busyId === m.id}
                    data-testid={`opsRosterEdit-${m.id}`}
                    onClick={() => {
                      setRowError(null);
                      setEditing({ kind: 'edit', id: m.id });
                    }}
                  >
                    Edit
                  </LinkButton>
                  <LinkButton
                    type="button"
                    aria-label={
                      busyId === m.id
                        ? `Saving ${m.full_name}`
                        : `${m.active ? 'Deactivate' : 'Reactivate'} ${m.full_name}`
                    }
                    // No row action while the add / edit form is open.
                    disabled={formOpen || busyId !== null}
                    data-testid={`opsRosterToggle-${m.id}`}
                    onClick={() => toggle(m)}
                  >
                    {busyId === m.id
                      ? 'Saving…'
                      : m.active
                        ? 'Deactivate'
                        : 'Reactivate'}
                  </LinkButton>
                  <DangerLink
                    type="button"
                    aria-label={`Delete ${m.full_name}`}
                    disabled={formOpen || busyId === m.id}
                    data-testid={`opsRosterDelete-${m.id}`}
                    onClick={() => {
                      setRowError(null);
                      setDeleting(m);
                    }}
                  >
                    Delete
                  </DangerLink>
                </RowActions>
              </RosterRowMain>
              {rowError?.id === m.id && (
                <ErrorList
                  messages={rowError.messages}
                  testId={`opsRosterRowError-${m.id}`}
                />
              )}
            </RosterRow>
          ),
        )}
      </RosterList>
    );
  }

  const cancelDelete = () => {
    if (!deleting || remove.isPending) return;
    setRefocus(`opsRosterDelete-${deleting.id}`);
    setDeleting(null);
  };

  // The delete confirm replaces the roster body inside this dialog (no
  // second modal on top). Cancel, the X and Esc go back to the list; a 422
  // ("<Name> has schedules. Deactivate instead.") shows under the row.
  const confirmView = deleting && (
    <>
      <DialogContent data-testid="opsRosterDeleteDialog">
        <DialogMessage>{deleteManagerText(deleting.full_name)}</DialogMessage>
      </DialogContent>
      <DialogFooter>
        <TextButton
          type="button"
          // Its Delete link is gone with the list: focus lands here.
          autoFocus
          disabled={remove.isPending}
          data-testid="opsRosterDeleteCancel"
          onClick={cancelDelete}
        >
          Cancel
        </TextButton>
        <DangerButton
          type="button"
          disabled={remove.isPending}
          data-testid="opsRosterDeleteConfirm"
          onClick={confirmDelete}
        >
          Delete
        </DangerButton>
      </DialogFooter>
    </>
  );

  return (
    <OpsDialog
      title={deleting ? DELETE_MANAGER_TITLE : ROSTER_TITLE}
      testId="opsRosterModal"
      returnFocus={returnFocus}
      closeDisabled={Boolean(deleting) && remove.isPending}
      onClose={deleting ? cancelDelete : onClose}
    >
      {confirmView || (
        <DialogContent>
          <Toolbar>
            <Muted css={{ fontSize: '14px' }}>
              Managers you can put on the ops schedule.
            </Muted>
            <SecondaryButton
              type="button"
              disabled={formOpen || query.isPending}
              data-testid="opsRosterAdd"
              onClick={() => {
                setRowError(null);
                setEditing({ kind: 'add' });
              }}
            >
              Add manager
            </SecondaryButton>
          </Toolbar>
          {editing?.kind === 'add' && (
            <RosterManagerForm onCancel={closeForm} onSaved={closeForm} />
          )}
          {list}
        </DialogContent>
      )}
    </OpsDialog>
  );
};

export default RosterModal;
